// WHAT: React Context wrapper for reminders due count
// WHY:  Provides simplified access to due reminders count from productivity domain
// DOMAIN: Shared → Contexts
// DEPENDENCIES: useReminders hook from productivity domain
// RISKS:
//   - NOTE: Creates dependency on productivity domain - may cause circular dependencies
//   - NOTE: Date comparison uses ISO string split - may have timezone issues
//   - NOTE: Very minimal wrapper - may need additional reminder features

import type { ReactNode } from "react";

import { useReminders } from "@/domains/productivity/reminders/hooks/useReminders";

import { RemindersContext } from "./RemindersContext.shared";

/**
 * WHAT:
 *   Provider component that calculates and provides the count of due reminders
 *   by filtering reminders from the productivity domain.
 *
 * WHY:
 *   Provides a simplified interface for accessing due reminders count without
 *   directly depending on the productivity domain in shared components.
 *
 * BEHAVIOR:
 *   - Uses useReminders hook from productivity domain
 *   - Filters reminders that are not done and have next_due_date <= today
 *   - Provides count of due reminders to context consumers
 *
 * RISKS:
 *   - NOTE: Creates dependency on productivity domain - may cause circular dependencies
 *   - NOTE: Date comparison uses ISO string split - may have timezone issues
 *   - NOTE: Very minimal wrapper - may need additional reminder features
 *   - NOTE: Filter runs on every render - may need memoization for performance
 *
 * @param root0 - Props object
 *
 * @param root0.children - Child components
 *
 * @returns RemindersProvider component
 *
 */
export const RemindersProvider = ({ children }: { children: ReactNode }) => {
  const remindersHook = useReminders as () => {
    reminders: { is_done: boolean; next_due_date?: null | string }[];
  };
  const { reminders } = remindersHook();
  const today: string = new Date().toISOString().split("T")[0] ?? "";
  const due = reminders.filter(
    (r) => !r.is_done && r.next_due_date && r.next_due_date <= today,
  ).length;

  return <RemindersContext.Provider value={{ due }}>{children}</RemindersContext.Provider>;
};
